import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  makeStyles, mergeClasses, tokens,
  Field, Input, Textarea, Checkbox, Button, Spinner, Dropdown, Option,
} from '@fluentui/react-components';
import { PAYSERA_URL } from '../config.js';
import { apiPost, apiGet } from '../services/api';
import { formatPrice } from '../utils/format';
import { Heading } from '../theme/primitives';
import { iconMap } from '../theme/icons';
import { stonePalette } from '../theme/brand';
import SelectionBoxLarge from '../configurator/components/blocks/SelectionBoxLarge';
import LegalTextDialog from '../components/LegalTextDialog';
import carpenterVisitImg from '../assets/img/marketing/carpenter-visit.jpg';
import {
  FURNITURE_TYPES, VISIT_PRICES, isFurnitureItemComplete, emptyFurnitureItem, calculateVisitPrice,
} from '../utils/carpenterVisitPricing';

const ErrorIcon = iconMap.error;
const LockIcon = iconMap.lock;
const CreditCardIcon = iconMap.credit_card;
const HomeRepairIcon = iconMap.home_repair_service;
const CheckIcon = iconMap.check;
const ScheduleIcon = iconMap.schedule;
const VerifiedIcon = iconMap.verified_user;
const PersonIcon = iconMap.person;
const LocationAwayIcon = iconMap.location_away;
const AddIcon = iconMap.add;
const RemoveIcon = iconMap.remove;
const ArrowBackIcon = iconMap.arrow_back;

const fmt = (v) => formatPrice(v, { showCents: true });

// Plonas apvalkalas ties realiais Fluent `Field`+`Input`/`Textarea` — ta pati
// signatūra (label/rows/...) kaip senojo lokalaus helperio, žr. Checkout.jsx.
// `validationState`/`validationMessage` keliauja į `Field` (ne į Input/Textarea) —
// žr. 40 punktų sąrašo site #4: klaidos rodomos PRIE SAVO lauko, ne bendroje
// MessageBar juostoje.
function FormField({ label, rows, validationState, validationMessage, ...props }) {
  return (
    <Field label={label} validationState={validationState} validationMessage={validationMessage}>
      {rows
        ? <Textarea resize="none" rows={rows} {...props} />
        : <Input {...props} />}
    </Field>
  );
}

// Griffel keyframe objektas tiesiai `animationName` reikšmėje — žr. Checkout.jsx
// pastabą: @griffel/core resolveStyleRules apdoroja šį atvejį be atskiro helperio.
const slideDownKeyframes = {
  from: { opacity: 0, transform: 'translateY(-6px)' },
  to: { opacity: 1, transform: 'translateY(0)' },
};

// ClickUp "Meistras į namus. Papildomi klausimai matavimams vietoje." — formą
// perrašėme iš vieno statiško 30€ puslapio į 3 žingsnių vedlį (paslauga → baldai
// → duomenys), sekant AI mockup'o ("Matavimai vietoje.html") struktūrą/logiką,
// bet PERKELTA į esamą Fluent/Griffel komponentų ir stiliaus sistemą (SelectionBoxLarge
// kortelės, Checkout.jsx nuolaidos kodo blokas), ne raw HTML/CSS iš mockup'o.
const STAGE_KEYS = { service: 'carpenter.label_service', furniture: 'carpenter.label_furniture', details: 'carpenter.label_details' };

const useStyles = makeStyles({
  root: { backgroundColor: stonePalette[50] },

  // Balta (ne paveldėta stonePalette[50]) — sekcija su nuotrauka turi būti
  // balta visuose šios struktūros puslapiuose (žr. #8: /ekspozicija,
  // /dovanu-cekis, /meistro-uzsakymas ir kt.).
  hero: {
    backgroundColor: tokens.colorNeutralBackground1,
    paddingTop: tokens.spacingVerticalXXXL, paddingBottom: tokens.spacingVerticalXXXL,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  heroInner: {
    maxWidth: '80rem', marginLeft: 'auto', marginRight: 'auto',
    paddingLeft: tokens.spacingHorizontalXXL, paddingRight: tokens.spacingHorizontalXXL,
    display: 'grid', gridTemplateColumns: '1fr', gap: tokens.spacingHorizontalXXL, alignItems: 'center',
    '@media (min-width: 900px)': { gridTemplateColumns: '1fr 1fr' },
    '@media (min-width: 1024px)': { paddingLeft: '3rem', paddingRight: '3rem' },
  },
  title: { marginBottom: tokens.spacingVerticalM },
  heroText: { fontSize: tokens.fontSizeBase400, color: tokens.colorNeutralForeground2, lineHeight: '1.6', marginBottom: tokens.spacingVerticalL, maxWidth: '32rem' },
  heroPoints: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  heroPoint: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS, fontSize: tokens.fontSizeBase300, color: tokens.colorNeutralForeground1 },
  heroPointIcon: { color: tokens.colorBrandForeground2, fontSize: '18px', flexShrink: 0 },
  heroImageWrap: {
    borderRadius: tokens.borderRadiusXLarge, overflow: 'hidden', boxShadow: tokens.shadow16,
    aspectRatio: '4 / 3',
  },
  heroImage: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },

  section: { paddingTop: '4rem', paddingBottom: '4rem', backgroundColor: tokens.colorNeutralBackground2 },
  sectionInner: {
    maxWidth: '80rem', marginLeft: 'auto', marginRight: 'auto',
    paddingLeft: tokens.spacingHorizontalXXL, paddingRight: tokens.spacingHorizontalXXL,
    '@media (min-width: 1024px)': { paddingLeft: '3rem', paddingRight: '3rem' },
  },

  // 1.6fr/1fr — tas pats santykis, kaip mockup'o .layout, bet kortelių/tarpų
  // stilius čia visur Fluent (žr. card/cardTitle žemiau), ne mockup'o raw CSS.
  layout: {
    display: 'grid', gridTemplateColumns: '1fr', gap: tokens.spacingHorizontalXXL, alignItems: 'start',
    '@media (min-width: 1024px)': { gridTemplateColumns: '1.6fr 1fr' },
  },
  leftCol: { minWidth: 0, display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL },
  asideCol: { position: 'sticky', top: tokens.spacingVerticalXXL },

  card: {
    backgroundColor: tokens.colorNeutralBackground1, border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusXLarge, padding: tokens.spacingHorizontalXXL,
    display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL,
  },
  cardTitle: {
    fontWeight: tokens.fontWeightSemibold, color: tokens.colorNeutralForeground1,
    paddingBottom: tokens.spacingVerticalM, borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  hint: { fontSize: tokens.fontSizeBase300, color: tokens.colorNeutralForeground3, margin: 0, marginTop: `calc(-1 * ${tokens.spacingVerticalM})` },

  backBtn: { alignSelf: 'flex-start', paddingLeft: 0 },

  // Žingsnio progreso juosta — tie patys taškai/linija, kaip mockup'o .progress,
  // bet dėliojama tokens/makeStyles, ne raw CSS.
  progress: { display: 'flex', alignItems: 'flex-start' },
  progressItem: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: tokens.spacingVerticalXXS, minWidth: '84px', textAlign: 'center' },
  progressDot: {
    width: '20px', height: '20px', borderRadius: tokens.borderRadiusCircular,
    border: `1.5px solid ${tokens.colorNeutralStroke2}`, backgroundColor: tokens.colorNeutralBackground1,
    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', color: '#fff',
  },
  progressDotActive: { borderColor: tokens.colorBrandStroke1, backgroundColor: tokens.colorBrandBackground },
  progressLabel: { fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3 },
  progressLabelActive: { color: tokens.colorNeutralForeground1, fontWeight: tokens.fontWeightSemibold },
  progressLine: { flexGrow: 1, height: '1.5px', backgroundColor: tokens.colorNeutralStroke2, marginTop: '10px' },

  visitRow: {
    display: 'grid', gridTemplateColumns: '1fr 1fr', gap: tokens.spacingHorizontalM,
    '@media (max-width: 520px)': { gridTemplateColumns: '1fr' },
  },
  visitPrice: { fontFamily: tokens.fontFamilyBase, fontSize: tokens.fontSizeBase500, fontWeight: tokens.fontWeightSemibold, color: tokens.colorBrandForeground2 },

  warnNote: { fontStyle: 'italic', fontSize: tokens.fontSizeBase200, color: tokens.colorPaletteBlueForeground2, marginLeft: '24px', marginTop: 0, marginBottom: 0 },

  stepperRow: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalM, flexWrap: 'wrap' },
  stepper: { display: 'flex', alignItems: 'center', border: `1px solid ${tokens.colorNeutralStroke2}`, borderRadius: tokens.borderRadiusMedium, overflow: 'hidden' },
  stepperCount: { minWidth: '36px', textAlign: 'center', fontWeight: tokens.fontWeightSemibold },
  stepperHint: { fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3 },

  furnitureList: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
  furnitureRow: {
    border: `1px dashed ${tokens.colorNeutralStroke2}`, borderRadius: tokens.borderRadiusXLarge,
    padding: tokens.spacingHorizontalL, display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS,
  },
  furnitureRowTitle: { fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3 },
  vizNote: { fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3, marginLeft: '24px', marginTop: 0, marginBottom: 0 },

  companyToggle: { cursor: 'pointer' },
  companyFields: {
    display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM,
    paddingTop: tokens.spacingVerticalS, borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    animationName: slideDownKeyframes, animationDuration: tokens.durationFast, animationTimingFunction: 'ease-out',
  },

  serviceCard: { backgroundColor: tokens.colorBrandBackground2, borderRadius: tokens.borderRadius3XLarge, padding: tokens.spacingHorizontalL },
  serviceCardHeader: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS, marginBottom: tokens.spacingVerticalS },
  serviceCardIcon: { fontSize: '20px', color: tokens.colorBrandForeground2 },
  serviceCardTitle: { fontWeight: tokens.fontWeightSemibold, color: tokens.colorNeutralForeground1, fontSize: tokens.fontSizeBase300 },
  serviceCardText: { fontSize: tokens.fontSizeBase300, color: tokens.colorNeutralForeground2, lineHeight: '1.5' },

  summaryBox: { backgroundColor: tokens.colorNeutralBackground2, borderRadius: tokens.borderRadius3XLarge, padding: tokens.spacingHorizontalL, display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS, fontSize: tokens.fontSizeBase300 },
  summaryRow: { display: 'flex', justifyContent: 'space-between', gap: tokens.spacingHorizontalS, color: tokens.colorNeutralForeground2 },
  summaryValue: { fontWeight: tokens.fontWeightSemibold, flexShrink: 0 },
  summaryDivider: { height: '1px', backgroundColor: tokens.colorNeutralStroke2 },
  summaryTotalRow: { display: 'flex', justifyContent: 'space-between', fontWeight: tokens.fontWeightBold, color: tokens.colorNeutralForeground1, fontSize: tokens.fontSizeBase400 },
  summaryTotalValue: { color: tokens.colorBrandForeground2 },

  deductBox: {
    marginTop: 0, padding: tokens.spacingHorizontalM, backgroundColor: tokens.colorNeutralBackground2,
    borderLeft: `2px solid ${tokens.colorBrandStroke1}`, borderRadius: tokens.borderRadiusMedium,
    fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground2,
    display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS,
  },

  // Tas pats nuolaidos kodo bloko stilius/klasių pavadinimai, kaip Checkout.jsx —
  // vartotojas turėtų atpažinti identišką elgesį abiejuose puslapiuose.
  discountCheckboxRow: { marginBottom: 0 },
  discountReveal: { animationName: slideDownKeyframes, animationDuration: tokens.durationFast, animationTimingFunction: 'ease-out' },
  discountInputRow: { display: 'flex', gap: tokens.spacingHorizontalS },
  discountInput: { flexGrow: 1 },
  dangerOutlineBtn: {
    borderColor: tokens.colorStatusDangerBorder1, color: tokens.colorStatusDangerForeground1,
    ':hover': { backgroundColor: tokens.colorStatusDangerBackground1, borderColor: tokens.colorStatusDangerBorder1, color: tokens.colorStatusDangerForeground1 },
  },
  whitespaceNoWrap: { whiteSpace: 'nowrap' },
  errorText: { fontSize: tokens.fontSizeBase200, color: tokens.colorStatusDangerForeground1, marginTop: tokens.spacingVerticalXS, marginBottom: 0 },
  successText: { fontSize: tokens.fontSizeBase200, color: tokens.colorBrandForeground2, marginTop: tokens.spacingVerticalXS, marginBottom: 0 },

  paymentLabel: { fontSize: tokens.fontSizeBase200, fontWeight: tokens.fontWeightSemibold, color: tokens.colorNeutralForeground3, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: tokens.spacingVerticalXS },
  paymentBox: { borderRadius: tokens.borderRadiusXLarge, border: `1px solid ${tokens.colorNeutralStroke2}`, padding: tokens.spacingHorizontalM, display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalM },
  paymentIcon: { fontSize: '20px', color: tokens.colorBrandForeground2 },
  paymentText: { fontSize: tokens.fontSizeBase300, color: tokens.colorNeutralForeground2 },

  errorBox: {
    display: 'flex', alignItems: 'flex-start', gap: tokens.spacingHorizontalS, padding: tokens.spacingHorizontalM,
    backgroundColor: tokens.colorStatusDangerBackground1, border: `1px solid ${tokens.colorStatusDangerBorder1}`,
    borderRadius: tokens.borderRadiusXLarge, fontSize: tokens.fontSizeBase300, color: tokens.colorStatusDangerForeground1,
  },
  errorIcon: { fontSize: '15px', flexShrink: 0, marginTop: '2px' },

  agreeRow: { display: 'flex', alignItems: 'flex-start', gap: tokens.spacingHorizontalS, cursor: 'pointer' },
  agreeLabel: { fontSize: tokens.fontSizeBase300, color: tokens.colorNeutralForeground2, lineHeight: '1.5' },
  // Tas pats stilius/elgesys kaip Checkout.jsx — taisyklės atsidaro dialog
  // lange (src/components/LegalTextDialog.jsx), ne atskirame puslapyje, kad
  // vartotojas neprarastų jau užpildytos formos.
  legalLink: {
    color: tokens.colorBrandForegroundLink, textDecorationLine: 'underline', textUnderlineOffset: '2px',
    cursor: 'pointer', border: 'none', background: 'none', padding: 0, font: 'inherit', fontSize: 'inherit',
    ':hover': { color: tokens.colorBrandForegroundLinkHover, textDecorationLine: 'none' },
  },

  submitBtn: { width: '100%', justifyContent: 'center', paddingTop: tokens.spacingVerticalL, paddingBottom: tokens.spacingVerticalL },
});

function Carpenter() {
  const { t } = useTranslation();
  const s = useStyles();
  useEffect(() => { document.title = `${t('carpenter.title')} | Fitit`; }, [t]);

  const [stage, setStage] = useState('service');
  const [visitType, setVisitType] = useState(null);
  const [afterHours, setAfterHours] = useState(false);
  const [furniture, setFurniture] = useState([emptyFurnitureItem()]);

  const [showCompanyFields, setShowCompanyFields] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [legalDialogSlug, setLegalDialogSlug] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  const [hasDiscountCode, setHasDiscountCode] = useState(false);
  const [discountCodeInput, setDiscountCodeInput] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState(null);
  const [discountError, setDiscountError] = useState('');

  const [form, setForm] = useState({
    name: '', surname: '', email: '', phone: '',
    city: '', address: '', postalCode: '', comments: '',
    company: { name: '', code: '', address: '', vatCode: '' },
  });

  const flow = visitType === 'self' ? ['service', 'details'] : ['service', 'furniture', 'details'];
  const stageIndex = flow.indexOf(stage);

  const pricing = useMemo(() => calculateVisitPrice({
    visitType: visitType || 'master',
    furnitureItems: furniture,
    afterHours,
    discountAmount: appliedDiscount?.amount || 0,
  }), [visitType, furniture, afterHours, appliedDiscount]);

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    setFieldErrors(prev => (prev[name] ? { ...prev, [name]: undefined } : prev));
  };
  const handleCompanyChange = e => setForm(prev => ({ ...prev, company: { ...prev.company, [e.target.name]: e.target.value } }));

  const applyDiscountCode = async () => {
    setDiscountError('');
    if (!discountCodeInput.trim()) return;
    try {
      const d = await apiGet(`/discount-codes/validate/${encodeURIComponent(discountCodeInput.trim())}`);
      setAppliedDiscount({ code: d.code, amount: d.amount });
    } catch (err) {
      setAppliedDiscount(null);
      setDiscountError(err.message || t('carpenter.invalid_discount_code'));
    }
  };

  const changeCount = (delta) => {
    setFurniture(prev => {
      const next = Math.min(10, Math.max(1, prev.length + delta));
      if (next === prev.length) return prev;
      return next > prev.length
        ? [...prev, ...Array.from({ length: next - prev.length }, emptyFurnitureItem)]
        : prev.slice(0, next);
    });
  };
  const updateFurnitureItem = (i, patch) => setFurniture(prev => prev.map((f, idx) => (idx === i ? { ...f, ...patch } : f)));

  const furnitureIncomplete = () => furniture.some(f => !isFurnitureItemComplete(f));
  const detailsIncomplete = () => {
    if (!form.name.trim() || !form.phone.trim() || !form.email.trim()) return true;
    if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) return true;
    if (form.phone.replace(/\D/g, '').length < 8) return true;
    if (!form.city.trim() || !form.address.trim() || !form.postalCode.trim()) return true;
    if (showCompanyFields && (!form.company.name.trim() || !form.company.code.trim() || !form.company.address.trim())) return true;
    return false;
  };
  const canProceed = () => {
    if (!visitType) return false;
    if (stage === 'furniture') return !furnitureIncomplete();
    if (stage === 'details') return !detailsIncomplete() && agreed;
    return true;
  };

  // Žingsnio keitimas (Tęsti/Atgal) TYČIA nejudina scroll pozicijos — vartotojas:
  // "puslapis neturi nusiskrolint į viršų". Anksčiau čia (ir handleSubmit žemiau)
  // buvo window.scrollTo({top:0}), kuris šokinėdavo atgal prie hero paveikslėlio
  // kaskart paspaudus "Tęsti užsakymą"/"Atgal" — pašalinta visai, ne tik
  // "pataisyta" (ankstesnis fix'as šį scroll'inimą padarė TIK patikimesnį, kas
  // buvo neteisinga kryptis — žr. git istoriją).
  const goBack = () => {
    const idx = flow.indexOf(stage);
    if (idx > 0) setStage(flow[idx - 1]);
  };

  const selectVisit = (v) => {
    setVisitType(v);
    if (v === 'self') setAfterHours(false);
  };

  const submitPayseraForm = (name, email, amountEur) => {
    const f = document.createElement('form');
    f.method = 'POST';
    f.action = PAYSERA_URL;
    [['name', name], ['email', email], ['amount', Math.round(amountEur * 100)]].forEach(([k, v]) => {
      const inp = document.createElement('input'); inp.type = 'hidden'; inp.name = k; inp.value = v; f.appendChild(inp);
    });
    document.body.appendChild(f); f.submit();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canProceed()) return;
    if (stage !== 'details') {
      const idx = flow.indexOf(stage);
      setStage(flow[idx + 1]);
      return;
    }
    const errors = {};
    if (!form.name.trim()) errors.name = t('carpenter.error_required');
    if (!form.email.trim()) errors.email = t('carpenter.error_required');
    if (!form.phone.trim()) errors.phone = t('carpenter.error_required');
    if (Object.keys(errors).length > 0) { setFieldErrors(errors); return; }
    setFieldErrors({});
    if (!agreed) { setError(t('carpenter.error_agree')); return; }
    setLoading(true); setError('');
    try {
      await apiPost('/carpenter', {
        ...form, company: showCompanyFields ? form.company : null,
        visitType, afterHours, furnitureItems: visitType === 'master' ? furniture : [],
        discountCode: appliedDiscount?.code || '', pricing,
      });
      submitPayseraForm(form.name, form.email, pricing.total);
    } catch (err) { setError(err.message); setLoading(false); }
  };

  return (
    <div className={s.root}>

      {/* Hero */}
      <div className={s.hero}>
        <div className={s.heroInner}>
          <div>
            <Heading as="h1" variant="h1" className={s.title}>{t('carpenter.title')}</Heading>
            <p className={s.heroText}>{t('carpenter.hero_text')}</p>
            <div className={s.heroPoints}>
              <div className={s.heroPoint}><VerifiedIcon className={s.heroPointIcon} />{t('carpenter.hero_point_0')}</div>
              <div className={s.heroPoint}><ScheduleIcon className={s.heroPointIcon} />{t('carpenter.hero_point_1')}</div>
              <div className={s.heroPoint}><CheckIcon className={s.heroPointIcon} />{t('carpenter.hero_point_2')}</div>
            </div>
          </div>
          <div className={s.heroImageWrap}>
            <img src={carpenterVisitImg} alt={t('carpenter.img_alt')} className={s.heroImage} />
          </div>
        </div>
      </div>

      {/* Form */}
      <section className={s.section}>
        <div className={s.sectionInner}>
          <form onSubmit={handleSubmit}>
            <div className={s.layout}>

              <div className={s.leftCol}>

                <div className={s.progress}>
                  {flow.map((st, i) => (
                    <React.Fragment key={st}>
                      <div className={s.progressItem}>
                        <span className={mergeClasses(s.progressDot, i <= stageIndex && s.progressDotActive)}>
                          {i < stageIndex ? '✓' : ''}
                        </span>
                        <span className={mergeClasses(s.progressLabel, i === stageIndex && s.progressLabelActive)}>{t(STAGE_KEYS[st])}</span>
                      </div>
                      {i < flow.length - 1 && <div className={s.progressLine} />}
                    </React.Fragment>
                  ))}
                </div>

                {stage !== 'service' && (
                  <Button appearance="transparent" icon={<ArrowBackIcon />} onClick={goBack} className={s.backBtn}>
                    {t('carpenter.back')}
                  </Button>
                )}

                {stage === 'service' && (
                  <div className={s.card}>
                    <Heading as="h3" variant="h3" className={s.cardTitle}>{t('carpenter.stage_service_title')}</Heading>
                    <p className={s.hint}>{t('carpenter.stage_service_hint')}</p>
                    <div className={s.visitRow}>
                      <SelectionBoxLarge
                        title={t('carpenter.visit_self_title')}
                        subTitle={t('carpenter.visit_self_time')}
                        selected={visitType === 'self'}
                        onClick={() => selectVisit('self')}
                        icon={<PersonIcon style={{ fontSize: '20px' }} />}
                        footer={<div className={s.visitPrice}>{fmt(VISIT_PRICES.self)}</div>}
                      />
                      <SelectionBoxLarge
                        title={t('carpenter.visit_master_title')}
                        subTitle={t('carpenter.visit_master_time')}
                        selected={visitType === 'master'}
                        onClick={() => selectVisit('master')}
                        icon={<LocationAwayIcon style={{ fontSize: '20px' }} />}
                        footer={<div className={s.visitPrice}>{t('carpenter.visit_master_from', { amount: fmt(VISIT_PRICES.masterBase) })}</div>}
                      />
                    </div>
                    {visitType === 'master' && (
                      <div>
                        <Checkbox
                          checked={afterHours}
                          onChange={(_, d) => setAfterHours(!!d.checked)}
                          label={t('carpenter.after_hours_label', { amount: fmt(VISIT_PRICES.afterHours) })}
                        />
                        {afterHours && <p className={s.warnNote}>{t('carpenter.after_hours_note')}</p>}
                      </div>
                    )}
                  </div>
                )}

                {stage === 'furniture' && (
                  <div className={s.card}>
                    <Heading as="h3" variant="h3" className={s.cardTitle}>{t('carpenter.stage_furniture_title')}</Heading>
                    <p className={s.hint}>{t('carpenter.stage_furniture_hint')}</p>
                    <div className={s.stepperRow}>
                      <div className={s.stepper}>
                        <Button appearance="subtle" icon={<RemoveIcon />} aria-label={t('carpenter.remove_item')} onClick={() => changeCount(-1)} disabled={furniture.length <= 1} />
                        <span className={s.stepperCount}>{furniture.length}</span>
                        <Button appearance="subtle" icon={<AddIcon />} aria-label={t('carpenter.add_item')} onClick={() => changeCount(1)} disabled={furniture.length >= 10} />
                      </div>
                      <span className={s.stepperHint}>{t('carpenter.extra_item_hint', { amount: fmt(VISIT_PRICES.extraItem) })}</span>
                    </div>
                    <div className={s.furnitureList}>
                      {furniture.map((f, i) => (
                        <div key={i} className={s.furnitureRow}>
                          <span className={s.furnitureRowTitle}>{t('carpenter.furniture_n', { n: i + 1 })}</span>
                          <Dropdown
                            placeholder={t('carpenter.furniture_type_ph')}
                            value={f.type}
                            selectedOptions={f.type ? [f.type] : []}
                            onOptionSelect={(_, d) => updateFurnitureItem(i, { type: d.optionValue || '', customType: d.optionValue === 'Kita' ? f.customType : '' })}
                          >
                            {FURNITURE_TYPES.map(ft => (
                              <Option key={ft} value={ft}>{ft}{ft === 'Virtuvė' ? ` (+${fmt(VISIT_PRICES.kitchen)})` : ''}</Option>
                            ))}
                          </Dropdown>
                          {f.type === 'Kita' && (
                            <Input
                              placeholder={t('carpenter.furniture_custom_ph')}
                              value={f.customType}
                              onChange={e => updateFurnitureItem(i, { customType: e.target.value })}
                            />
                          )}
                          <Checkbox
                            checked={f.viz}
                            onChange={(_, d) => updateFurnitureItem(i, { viz: !!d.checked })}
                            label={t('carpenter.viz_label', { amount: fmt(VISIT_PRICES.viz) })}
                          />
                          {f.viz && (
                            <>
                              <p className={s.vizNote}>{t('carpenter.viz_note_1')}</p>
                              <p className={s.vizNote}>{t('carpenter.viz_note_2')}</p>
                              <p className={s.warnNote}>{t('carpenter.viz_note_warn')}</p>
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {stage === 'details' && (
                  <>
                    <div className={s.card}>
                      <Heading as="h3" variant="h3" className={s.cardTitle}>{t('carpenter.section_personal')}</Heading>
                      <FormField label={`${t('carpenter.name')} *`} name="name" value={form.name} onChange={handleChange} placeholder="Jonas" required validationState={fieldErrors.name ? 'error' : undefined} validationMessage={fieldErrors.name} />
                      <FormField label={t('carpenter.surname')} name="surname" value={form.surname} onChange={handleChange} placeholder="Jonaitis" />
                      <FormField label={`${t('carpenter.phone')} *`} name="phone" value={form.phone} onChange={handleChange} placeholder="+370 600 00000" required validationState={fieldErrors.phone ? 'error' : undefined} validationMessage={fieldErrors.phone} />
                      <FormField label={`${t('carpenter.email')} *`} name="email" type="email" value={form.email} onChange={handleChange} placeholder="jonas@pvz.lt" required validationState={fieldErrors.email ? 'error' : undefined} validationMessage={fieldErrors.email} />

                      <Checkbox
                        className={s.companyToggle}
                        checked={showCompanyFields}
                        onChange={(_, data) => setShowCompanyFields(!!data.checked)}
                        label={t('carpenter.buy_company')}
                      />

                      {showCompanyFields && (
                        <div className={s.companyFields}>
                          <FormField label={t('carpenter.company_name')} name="name" value={form.company.name} onChange={handleCompanyChange} placeholder="UAB Pavyzdys" />
                          <FormField label={t('carpenter.company_code')} name="code" value={form.company.code} onChange={handleCompanyChange} placeholder="300000000" />
                          <FormField label={t('carpenter.vat_code')} name="vatCode" value={form.company.vatCode} onChange={handleCompanyChange} placeholder="LT000000000" />
                          <FormField label={t('carpenter.company_address')} name="address" value={form.company.address} onChange={handleCompanyChange} placeholder="Gatvė, Vilnius" />
                        </div>
                      )}
                    </div>

                    <div className={s.card}>
                      <Heading as="h3" variant="h3" className={s.cardTitle}>
                        {visitType === 'self' ? t('carpenter.section_location_self') : t('carpenter.section_location')}
                      </Heading>
                      <FormField label={t('carpenter.city')} name="city" value={form.city} onChange={handleChange} placeholder="Vilnius" />
                      <FormField label={t('carpenter.address')} name="address" value={form.address} onChange={handleChange} placeholder="Gatvė, namas" />
                      <FormField label={t('carpenter.postal_code')} name="postalCode" value={form.postalCode} onChange={handleChange} placeholder="LT-00000" />
                      <FormField label={t('carpenter.comments')} name="comments" value={form.comments} onChange={handleChange} placeholder="Papildoma informacija..." rows={3} />
                    </div>
                  </>
                )}

              </div>

              {/* Summary — persistent throughout all stages */}
              <div className={s.asideCol}>
                <div className={s.card}>
                  <Heading as="h3" variant="h3" className={s.cardTitle}>{t('carpenter.section_summary')}</Heading>

                  {visitType && (
                    <div className={s.serviceCard}>
                      <div className={s.serviceCardHeader}>
                        <HomeRepairIcon className={s.serviceCardIcon} />
                        <span className={s.serviceCardTitle}>{visitType === 'self' ? t('carpenter.visit_self_title') : t('carpenter.visit_master_title')}</span>
                      </div>
                      <p className={s.serviceCardText}>{visitType === 'self' ? t('carpenter.visit_self_note') : t('carpenter.visit_master_note')}</p>
                    </div>
                  )}

                  <div className={s.summaryBox}>
                    {!visitType ? (
                      <div className={s.summaryRow}><span>{t('carpenter.choose_service')}</span><span>—</span></div>
                    ) : visitType === 'self' ? (
                      <div className={s.summaryRow}><span>{t('carpenter.visit_self_title')}</span><span className={s.summaryValue}>{fmt(pricing.base)}</span></div>
                    ) : (
                      <>
                        <div className={s.summaryRow}><span>{t('carpenter.line_base')}</span><span className={s.summaryValue}>{fmt(pricing.base)}</span></div>
                        {furniture.length > 1 && (
                          <div className={s.summaryRow}><span>{t('carpenter.line_extra', { count: furniture.length - 1 })}</span><span className={s.summaryValue}>{fmt(pricing.extra)}</span></div>
                        )}
                        {pricing.kitchenCount > 0 && (
                          <div className={s.summaryRow}><span>{t('carpenter.line_kitchen', { count: pricing.kitchenCount })}</span><span className={s.summaryValue}>{fmt(pricing.kitchenFeeTotal)}</span></div>
                        )}
                        {pricing.afterHoursFee > 0 && (
                          <div className={s.summaryRow}><span>{t('carpenter.line_after_hours')}</span><span className={s.summaryValue}>{fmt(pricing.afterHoursFee)}</span></div>
                        )}
                        {pricing.vizCount > 0 && (
                          <div className={s.summaryRow}><span>{t('carpenter.line_viz', { count: pricing.vizCount })}</span><span className={s.summaryValue}>{fmt(pricing.vizFee)}</span></div>
                        )}
                      </>
                    )}
                    {appliedDiscount && (
                      <div className={s.summaryRow}><span>{t('carpenter.line_discount', { code: appliedDiscount.code })}</span><span className={s.summaryValue}>-{fmt(appliedDiscount.amount)}</span></div>
                    )}
                    <div className={s.summaryDivider} />
                    <div className={s.summaryTotalRow}>
                      <span>{t('carpenter.total')}</span>
                      <span className={s.summaryTotalValue}>{visitType ? fmt(pricing.total) : '—'}</span>
                    </div>
                  </div>

                  {visitType && (
                    <div className={s.deductBox}>
                      <div>{t('carpenter.deduct_line', { amount: fmt(pricing.deductible) })}</div>
                      {(pricing.afterHoursFee > 0 || pricing.vizFee > 0) && <div>{t('carpenter.deduct_note')}</div>}
                    </div>
                  )}

                  {visitType && (
                    <div>
                      <Checkbox
                        className={s.discountCheckboxRow}
                        checked={hasDiscountCode}
                        onChange={(_, data) => setHasDiscountCode(!!data.checked)}
                        label={{ children: t('carpenter.has_discount'), className: s.agreeLabel }}
                      />
                      {hasDiscountCode && (
                        <div className={s.discountReveal}>
                          <div className={s.discountInputRow}>
                            <Input className={s.discountInput} placeholder={t('carpenter.discount_ph')} value={discountCodeInput} onChange={e => setDiscountCodeInput(e.target.value)} disabled={!!appliedDiscount} />
                            {!appliedDiscount
                              ? <Button appearance="outline" size="small" className={s.whitespaceNoWrap} onClick={applyDiscountCode}>{t('carpenter.apply')}</Button>
                              : <Button appearance="outline" size="small" className={s.dangerOutlineBtn} onClick={() => { setAppliedDiscount(null); setDiscountCodeInput(''); }}>✕</Button>}
                          </div>
                          {discountError && <p className={s.errorText}>{discountError}</p>}
                          {appliedDiscount && <p className={s.successText}>{t('carpenter.applied_amount', { amount: fmt(appliedDiscount.amount) })}</p>}
                        </div>
                      )}
                    </div>
                  )}

                  <div>
                    <p className={s.paymentLabel}>{t('common.payment_label')}</p>
                    <div className={s.paymentBox}>
                      <CreditCardIcon className={s.paymentIcon} />
                      <span className={s.paymentText}>{t('common.payment_paysera')}</span>
                    </div>
                  </div>

                  {error && (
                    <div className={s.errorBox}>
                      <ErrorIcon className={s.errorIcon} />
                      {error}
                    </div>
                  )}

                  {stage === 'details' && (
                    <Checkbox
                      className={s.agreeRow}
                      checked={agreed}
                      onChange={(_, data) => setAgreed(!!data.checked)}
                      label={{
                        children: (
                          <span className={s.agreeLabel}>
                            {t('carpenter.agree')}{' '}
                            <button type="button" className={s.legalLink} onClick={() => setLegalDialogSlug('taisykles')}>Sąlygomis ir taisyklėmis</button>
                          </span>
                        ),
                      }}
                    />
                  )}

                  <Button type="submit" appearance="primary" size="large" disabled={loading || !canProceed()} className={s.submitBtn}>
                    {loading
                      ? <Spinner size="tiny" />
                      : stage === 'details'
                        ? <><LockIcon fontSize={18} />{t('carpenter.btn_pay', { amount: fmt(pricing.total) })}</>
                        : t('carpenter.btn_continue')}
                  </Button>
                </div>
              </div>

            </div>
          </form>
        </div>
      </section>

      <LegalTextDialog slug={legalDialogSlug} onClose={() => setLegalDialogSlug(null)} />
    </div>
  );
}

export default Carpenter;
